import axios from 'axios'

import constants                    from '@/constants';

import { parseUsgsRdb, findSiteRefSetIntersection }                 from '@/common/functions';

import UsgsValuesApiResponse        from '@/api/UsgsValuesApiResponse';
import UsgsHistoricalApiResponse    from '@/api/UsgsHistoricalApiResponse';

import SiteReference from '@/models/SiteReference';
import CoordinatePair from '@/models/CoordinatePair';
import SiteData from '@/models/SiteData';

/**
 * List of USGS parameter codes to fetch in each request
 *
 * see https://help.waterdata.usgs.gov/codes-and-parameters/parameters & https://help.waterdata.usgs.gov/parameter_cd?group_cd=PHY
 */
const USGS_VARIABLES_TO_FETCH = [
  constants.FlowVariables.Discharge.parameterCd,
  constants.FlowVariables.GaugeHeight.parameterCd,
  constants.SecondaryVariables.WaterTemp.parameterCd,
];

/**
 * Axios wrapper for OSF API...
 *
 * WAIT, no, the axios wrapper for everything?
 *
 * ... I don't get it
 */
const fuckedUpApiClient = axios.create({

  // Heroku Instance (john@openstreamflow.com)
  baseURL: 'https://waterql.herokuapp.com/',

  // Old Heroku Instance (john@allthesedetails.com)
  // baseURL: 'https://fast-hamlet-36674.herokuapp.com/',

  // Local instance
  // baseURL: 'http://localhost:5000'
});

// ???
const execute = async (method: string, resource: string, data: any = undefined) => {
  const response = await fuckedUpApiClient({
    method,
    url: resource,
    data,
  });
  return response.data;
};

// this is weird, refactor plz?
const executeAndParseRDB = async (method: string, resource: string, data: any = undefined) => {
  const response = await fuckedUpApiClient({
    method,
    url: resource,
    data,
  });

  let sites = parseUsgsRdb(response.data);
  response.data = sites;
  return response.data;
}

export default {

  //
  // Open Stream Flow API Server Endpoints
  //

  getSites () {
    return execute('get', '/sites?q=')
  },
  async getSite (id: string) {
    const thing: any = await execute('get', `/sites/${id}`)
    return {
      ...thing,
      isNullSite: false
    }
  },

  /**
   * Find sites that match the given input
   */
  async getSitesWithKeyword(input: string): Promise<Array<SiteReference>> {

    // Split the query into multiple keywords
    const keywords = input.split(/\s+/).filter(s => s !== '');
    const promises: Array<Promise<Array<SiteReference>>> = [];

    keywords.forEach(keyword => {
      promises.push(execute('get', '/sites?q=' + keyword))
    });

    const results = await Promise.all(promises);

    return findSiteRefSetIntersection(results);

    // FIXME MAJOR SECURITY... Is this a sanitization bug? Probvably not here, but maybe on the server...
    // const results: Array<SiteReference> = await execute('get', '/sites?' + query);
    // return results;
  },

  /**
   * Find sites within the given geographical bounds
   */
  async getSitesForBounds(southwestBound: CoordinatePair, northeastBound: CoordinatePair): Promise<Array<SiteReference>> {

    let sw_lat = southwestBound.lat;
    let sw_lng = southwestBound.lng;

    let ne_lat = northeastBound.lat;
    let ne_lng = northeastBound.lng;

    const results: Array<SiteReference> = await execute('get', '/sites?sw_lat=' + sw_lat + '&sw_lng=' + sw_lng +
                                      '&ne_lat=' + ne_lat + '&ne_lng=' + ne_lng);
    return results;
  },

  //
  // USGS Site Service API Endpoints
  //
  // https://waterservices.usgs.gov/rest/Site-Service.html
  //

  /**
   * UNUSED IN THIS PROJECT!
   */
   getSiteInfo(siteNumber: string) {
    return executeAndParseRDB('get', "https://waterservices.usgs.gov/nwis/site/?format=rdb&site=" + siteNumber + "&siteOutput=expanded");
  },

  /**
   * UNUSED IN THIS PROJECT!
   */
  getSiteInfoForState(stateCd: string) {
    return executeAndParseRDB('get', "https://waterservices.usgs.gov/nwis/site/?format=rdb&period=P52W&stateCd=" + stateCd + "&siteOutput=expanded&siteType=ST&hasDataTypeCd=iv");
  },

  /**
   * Fetch data from all three of the USGS APIs we're using...
   *
   * Waits for all responses to come back before proceeding.
   */
  async getAllSiteData(siteReference: SiteReference): Promise<SiteData> {

    const siteNumber = siteReference.siteNumber;

    // Get data from all three USGS APIs
    const startTime = new Date();
    const ivSiteDataPromise = this.getIvSiteData({siteNumber, timePeriod: constants.TimePeriods.get("OneMonth")})
    const dvSiteDataPromise = this.getDvSiteData({siteNumber, timePeriod: constants.TimePeriods.get("OneYear")})
    const historicalDataPromise = this.getHistoricalSiteData({siteNumber})

    // Wait until they all come back
    const fulfilled = await Promise.all([ivSiteDataPromise, dvSiteDataPromise, historicalDataPromise]);

    const endTime = new Date();
    console.log(`time to fetch datasets for site ${siteNumber}: ${(endTime.getTime() - startTime.getTime()) / 1000} seconds`)

    // This is shitty, but AFAIK this is legit and a better approach than duck-typing all of the results, because Promise.all
    // returns things in the order in which they are in the array
    const ivSiteData = fulfilled[0];
    const dvSiteData = fulfilled[1];
    const historicalData = fulfilled[2];

    // Make the SiteData instancew
    const siteData = new SiteData();
    siteData.setIvDataSet(ivSiteData);
    siteData.setDvDataSet(dvSiteData);
    siteData.setHistoricalDataSet(historicalData);

    return siteData;
  },


  //
  // USGS Instantaneous Values Web Service API Endpoints
  //
  // https://waterservices.usgs.gov/rest/IV-Service.html
  //

  /**
   * Get USGS IV API data for all OSF supported variables at the given time period
   *
   * IV API is used for time periods <= 1 month
   */
  async getIvSiteData({timePeriod, siteNumber}: { timePeriod: any; siteNumber: string}): Promise<UsgsValuesApiResponse> {

    let parameterCodesString = '';
    if (USGS_VARIABLES_TO_FETCH.length > 0) {
      parameterCodesString = '&parameterCd=' + USGS_VARIABLES_TO_FETCH.join(',');
    }

    timePeriod = timePeriod || constants.TimePeriods.get('OneWeek');
    const timePeriodValue = timePeriod.value;

    // https://waterservices.usgs.gov/nwis/iv/?format=json&sites=" + site + "&parameterCd=00060&period=P7D
    // let resource = "https://waterservices.usgs.gov/nwis/iv/?format=json&sites=" + siteNumber +
    //                   "&period=" + timePeriodValue +
    //                   parameterCodesString;
    const resource = "https://waterservices.usgs.gov/nwis/iv/?format=json&sites=" + siteNumber +
                      "&period=" + timePeriodValue +
                      parameterCodesString;

    const rawData = await execute('get', resource);

    return new UsgsValuesApiResponse(timePeriod, rawData);
  },

  /**
   * Get USGS DV API data for all OSF supported variables at the given time period
   *
   * DV API is used for time periods >= 3 months
   */
  async getDvSiteData({timePeriod, siteNumber}: { timePeriod: any; siteNumber: string}): Promise<UsgsValuesApiResponse>  {

    let parameterCodesString = '';
    if (USGS_VARIABLES_TO_FETCH.length > 0) {
      parameterCodesString = '&parameterCd=' + USGS_VARIABLES_TO_FETCH.join(',');
    }

    timePeriod = timePeriod || constants.TimePeriods.get('OneWeek');
    const timePeriodValue = timePeriod.value;

    // https://waterservices.usgs.gov/nwis/iv/?format=json&sites=" + site + "&parameterCd=00060&period=P7D
    const resource = "https://waterservices.usgs.gov/nwis/dv/?format=json&sites=" + siteNumber +
                      "&period=" + timePeriodValue +
                      parameterCodesString;

    const rawData = await execute('get', resource);

    return new UsgsValuesApiResponse(timePeriod, rawData);
  },

  /**
   * Get USGS Historical API data for all variables the USGS tracks on the site
   *
   * Time period is always the full water year. This dataset comes back in a weird format,
   * and must be stitched / overlayed onto other time series for display.
   *
   * see https://waterservices.usgs.gov/rest/Statistics-Service-Test-Tool.html
   */
   async getHistoricalSiteData({siteNumber}: {siteNumber: string}): Promise<UsgsHistoricalApiResponse>  {
    const resource = "https://waterservices.usgs.gov/nwis/stat/?format=rdb&sites=" + siteNumber + "&statReportType=daily&statTypeCd=all";
    const rawData = await executeAndParseRDB('get', resource);

    const historicalData = new UsgsHistoricalApiResponse(rawData);
    return historicalData;
  }

}
